import React, { useEffect } from 'react';
import { clsx } from 'clsx';
import { Button } from '../../../components/ui/Button';

export interface ConfirmDialogProps {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  /** When true, confirm button uses danger styling */
  danger?: boolean;
  /** Disables actions and shows loading on the confirm button */
  isConfirmLoading?: boolean;
}

/**
 * Lightweight confirmation overlay (no external dialog library).
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  onConfirm,
  onCancel,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  isConfirmLoading = false,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isConfirmLoading) onCancel();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onCancel, isConfirmLoading]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 animate-fade-in"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-desc"
      onClick={isConfirmLoading ? undefined : onCancel}
    >
      <div
        className={clsx(
          'bg-app-surface dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md',
          'border border-app-border dark:border-gray-700',
          'animate-slide-up'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-2">
          <h2 id="confirm-dialog-title" className="text-lg font-semibold text-app-text dark:text-white">
            {title}
          </h2>
          <p id="confirm-dialog-desc" className="mt-2 text-sm text-app-text-muted dark:text-gray-400">
            {message}
          </p>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-5 py-4 border-t border-app-border dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            className="w-full sm:w-auto"
            disabled={isConfirmLoading}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={danger ? 'danger' : 'primary'}
            size="sm"
            onClick={onConfirm}
            className="w-full sm:w-auto"
            isLoading={isConfirmLoading}
            disabled={isConfirmLoading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};
